import React, { useState, cloneElement } from 'react';
import { ChevronDown, ChevronRight, Square, Settings, Zap, Wrench, Tag, MoreHorizontal, Cog } from 'lucide-react';
// Custom SVG icon for bearings - ball bearing with visible balls
function BearingIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-164">
      <circle cx="12" cy="12" r="10" data-id="element-165" />
      <circle cx="12" cy="12" r="4" data-id="element-166" />
      <circle cx="12" cy="4.5" r="1.5" fill="currentColor" stroke="none" data-id="element-167" />
      <circle cx="17.3" cy="6.7" r="1.5" fill="currentColor" stroke="none" data-id="element-168" />
      <circle cx="19.5" cy="12" r="1.5" fill="currentColor" stroke="none" data-id="element-169" />
      <circle cx="17.3" cy="17.3" r="1.5" fill="currentColor" stroke="none" data-id="element-170" />
      <circle cx="12" cy="19.5" r="1.5" fill="currentColor" stroke="none" data-id="element-171" />
      <circle cx="6.7" cy="17.3" r="1.5" fill="currentColor" stroke="none" data-id="element-172" />
      <circle cx="4.5" cy="12" r="1.5" fill="currentColor" stroke="none" data-id="element-173" />
      <circle cx="6.7" cy="6.7" r="1.5" fill="currentColor" stroke="none" data-id="element-174" />
    </svg>;
}
// Custom SVG icon for bearing units - pillow block style
function BearingUnitIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-175">
      <rect x="2" y="8" width="20" height="10" rx="2" data-id="element-176" />
      <circle cx="5" cy="15" r="1.5" data-id="element-177" />
      <circle cx="19" cy="15" r="1.5" data-id="element-178" />
      <circle cx="12" cy="11" r="4" data-id="element-179" />
      <circle cx="12" cy="11" r="1.5" fill="currentColor" stroke="none" data-id="element-180" />
    </svg>;
}
// Custom SVG icon for belts - V-belt cross section
function BeltIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-181">
      <circle cx="6" cy="12" r="4" data-id="element-182" />
      <circle cx="18" cy="12" r="4" data-id="element-183" />
      <line x1="6" y1="8" x2="18" y2="8" data-id="element-184" />
      <line x1="6" y1="16" x2="18" y2="16" data-id="element-185" />
      <circle cx="6" cy="12" r="1" fill="currentColor" stroke="none" data-id="element-186" />
      <circle cx="18" cy="12" r="1" fill="currentColor" stroke="none" data-id="element-187" />
    </svg>;
}
// Custom SVG icon for chain & sprockets
function ChainIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-188">
      <rect x="2" y="9" width="6" height="6" rx="2" data-id="element-189" />
      <rect x="9" y="9" width="6" height="6" rx="2" data-id="element-190" />
      <rect x="16" y="9" width="6" height="6" rx="2" data-id="element-191" />
      <circle cx="8" cy="12" r="1" fill="currentColor" stroke="none" data-id="element-192" />
      <circle cx="16" cy="12" r="1" fill="currentColor" stroke="none" data-id="element-193" />
    </svg>;
}
// Custom SVG icon for flange units - 4-bolt square flange with bearing
function FlangeUnitIcon({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" data-id="element-194">
      {/* Square housing */}
      <rect x="2" y="2" width="20" height="20" rx="2" data-id="element-195" />
      {/* 4 corner bolt holes */}
      <circle cx="5.5" cy="5.5" r="1.5" data-id="element-196" />
      <circle cx="18.5" cy="5.5" r="1.5" data-id="element-197" />
      <circle cx="5.5" cy="18.5" r="1.5" data-id="element-198" />
      <circle cx="18.5" cy="18.5" r="1.5" data-id="element-199" />
      {/* Central bearing - outer race */}
      <circle cx="12" cy="12" r="5" data-id="element-200" />
      {/* Central bearing - inner race */}
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" data-id="element-201" />
    </svg>;
}
interface Category {
  name: string;
  url: string;
}
interface CategoryGroup {
  title: string;
  icon: React.ReactNode;
  categories: Category[];
}
export function CategoryDirectory() {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (title: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };
  const categoryGroups: CategoryGroup[] = [{
    title: 'Bearings',
    icon: <BearingIcon className="w-7 h-7" data-id="element-202" />,
    categories: [{
      name: 'Agricultural Bearings',
      url: '/18m7/agricultural-bearings.html'
    }, {
      name: 'Appliance Bearings',
      url: '/18m7/appliance-bearings.html'
    }, {
      name: 'DLG Bearings',
      url: '/18m7/dlg-bearings.html'
    }, {
      name: 'Flanged Bearings',
      url: '/18m7/flanged-bearings.html'
    }, {
      name: 'Go-Kart Bearings',
      url: '/18m7/go-kart-bearings.html'
    }, {
      name: 'Insert Bearings',
      url: '/18m7/insert-bearings/uc-series.html'
    }, {
      name: 'Miniature Bearings',
      url: '/18m7/miniature-bearings.html'
    }, {
      name: 'Motorcycle Wheel Bearings',
      url: '/18m7/motorcycle-wheel-bearings.html'
    }, {
      name: 'Needle Bearings',
      url: '/18m7/needle-bearings.html'
    }, {
      name: 'R-Series Bearings',
      url: '/18m7/r-series-bearings.html'
    }, {
      name: 'Single Row Ball Bearings',
      url: '/18m7/single-row-ball-bearings/6000-ball-bearings.html'
    }, {
      name: 'Double Row Ball Bearings',
      url: '/18m7/double-row-ball-bearings/5200-series.html'
    }, {
      name: 'Taper Bearings',
      url: '/18m7/taper-roller-bearings.html'
    }, {
      name: 'Snowmobile Bearings',
      url: '/18m7/snowmobile-bearings.html'
    }, {
      name: 'Snow Blower / Snow Thrower Bearings',
      url: '/18m7/snow-blower-thrower-bearings-belts/snow-blower-thrower-bearings.html'
    }]
  }, {
    title: 'Bearing Units',
    icon: <BearingUnitIcon className="w-7 h-7" data-id="element-203" />,
    categories: [{
      name: 'Pillow Block Bearing Units',
      url: '/18m7/pillow-block-bearing-units/ucp-series-steel-units.html'
    }, {
      name: 'Plummer Block Bearing Units',
      url: '/18m7/plummer-block-bearing-units.html'
    }, {
      name: 'Take Up Bearings',
      url: '/18m7/take-up-bearings.html'
    }, {
      name: 'Tapped Base Bearings',
      url: '/18m7/tapped-base-bearings.html'
    }, {
      name: 'Round Flanged Cartridge Units',
      url: '/18m7/round-flanged-cartridge-units-ucfc-series.html'
    }]
  }, {
    title: 'Flange Units',
    icon: <FlangeUnitIcon className="w-7 h-7" data-id="element-204" />,
    categories: [{
      name: '2-Bolt Flange Units',
      url: '/18m7/2-bolt-flange-unit/steel-2-bolt-flange-units.html'
    }, {
      name: '4-Bolt Flange Units',
      url: '/18m7/4-bolt-flange-units/steel-4-bolt-flange-units.html'
    }]
  }, {
    title: 'Belts',
    icon: <BeltIcon className="w-7 h-7" data-id="element-205" />,
    categories: [{
      name: 'V-Belts',
      url: '/18m7/v-belts/type-a/type-a-v-belts.html'
    }, {
      name: 'Lawn Mower Belts',
      url: '/18m7/lawn-mower-bearings-belts/lawn-mower-belts.html'
    }, {
      name: 'Snow Blower / Snow Thrower Belts',
      url: '/18m7/snow-blower-thrower-bearings-belts/snow-blower-thrower-belts.html'
    }]
  }, {
    title: 'Pulleys & Sheaves',
    icon: <Cog className="w-7 h-7" data-id="element-206" />,
    categories: [{
      name: 'Pulleys & Sheaves',
      url: '/18m7/pulleys-sheaves.html'
    }, {
      name: 'Electric Motor Pulleys',
      url: '/18m7/pulleys-sheaves/electric-motor-pulleys/single-electric-motor-pulleys.html'
    }, {
      name: 'Variable Speed Pulleys',
      url: '/18m7/pulleys-sheaves/variable-speed-pulleys/vl-series.html'
    }]
  }, {
    title: 'Chain & Sprockets',
    icon: <ChainIcon className="w-7 h-7" data-id="element-207" />,
    categories: [{
      name: 'Chain',
      url: '/18m7/chain/40-chain.html'
    }, {
      name: 'Sprockets',
      url: '/18m7/sprockets/50-sprockets/a-plate-sprockets.html'
    }]
  }, {
    title: 'Couplings',
    icon: <Settings className="w-7 h-7" data-id="element-208" />,
    categories: [{
      name: 'Couplings',
      url: '/18m7/couplings/jaw-coupling.html'
    }]
  }, {
    title: 'Motors & Drives',
    icon: <Zap className="w-7 h-7" data-id="element-209" />,
    categories: [{
      name: 'Electric Motors',
      url: '/18m7/electric-motors.html'
    }, {
      name: 'Controls and Drives',
      url: '/18m7/controls-and-drives.html'
    }]
  }, {
    title: 'OEM / Equipment Parts',
    icon: <Wrench className="w-7 h-7" data-id="element-210" />,
    categories: [{
      name: 'Turf OEM Parts',
      url: '/18m7/turf-oem-parts/john-deere.html'
    }, {
      name: 'Lawn Mower Bearings',
      url: '/18m7/lawn-mower-bearings-belts/lawn-mower-bearings.html'
    }, {
      name: 'Lawn Mower Belts',
      url: '/18m7/lawn-mower-bearings-belts/lawn-mower-belts.html'
    }]
  }, {
    title: 'Specials',
    icon: <Tag className="w-7 h-7" data-id="element-211" />,
    categories: [{
      name: 'Special Sales',
      url: '/18m7/special-sales.html'
    }]
  }, {
    title: 'Other',
    icon: <MoreHorizontal className="w-7 h-7" data-id="element-212" />,
    categories: [{
      name: 'Cutler Oven Bearings and Parts',
      url: '/18m7/cutler-oven-bearings-and-parts.html'
    }, {
      name: 'Miscellaneous',
      url: '/18m7/miscellaneous.html'
    }]
  }];
  return <section className="bg-gray-50 py-16" data-id="element-213">
      <div className="max-w-7xl mx-auto px-4" data-id="element-214">
        <div className="text-center mb-12" data-id="element-215">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-216">
            Browse Products by Category
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-217">
            Browse our complete selection of industrial bearings, belts, power
            transmission components, and specialty products.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-218">
          {categoryGroups.map(group => {
          const isExpanded = expandedGroups.has(group.title);
          return <div key={group.title} className={`bg-white rounded-lg overflow-hidden transition-all duration-200 ${isExpanded ? 'border-2 border-red-600' : 'border border-gray-300 hover:border-red-500'}`} data-id="element-219">
                <button onClick={() => toggleGroup(group.title)} className={`w-full flex items-center gap-4 px-5 py-5 transition-colors min-h-[80px] ${isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'}`} aria-expanded={isExpanded} data-id="element-220">
                  <span className="text-red-700 flex-shrink-0" data-id="element-221">
                    {cloneElement(group.icon as React.ReactElement, {
                  className: 'w-9 h-9'
                })}
                  </span>
                  <span className="flex-1 text-left" data-id="element-222">
                    <span className="font-bold text-gray-900 text-lg block leading-tight" data-id="element-223">
                      {group.title}
                    </span>
                  </span>
                  <span className={`flex-shrink-0 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-224">
                    <ChevronRight className="w-6 h-6" data-id="element-225" />
                  </span>
                </button>

                <ul className={`border-t border-gray-200 bg-gray-50 ${isExpanded ? 'block' : 'hidden'}`} aria-hidden={!isExpanded} data-id="element-226">
                  {group.categories.map(category => <li key={category.url} className="border-b border-gray-100 last:border-b-0" data-id="element-227">
                      <a href={category.url} className="block px-4 py-2.5 text-sm text-blue-700 hover:text-blue-900 hover:bg-white transition-colors" data-id="element-228">
                        {category.name}
                      </a>
                    </li>)}
                </ul>
              </div>;
        })}
        </div>

        <nav className="sr-only" aria-label="All Product Categories" data-id="element-229">
          {categoryGroups.map(group => <div key={group.title} data-id="element-230">
              <h3 data-id="element-231">{group.title}</h3>
              <ul data-id="element-232">
                {group.categories.map(category => <li key={category.url} data-id="element-233">
                    <a href={category.url} data-id="element-234">{category.name}</a>
                  </li>)}
              </ul>
            </div>)}
        </nav>
      </div>
    </section>;
}
// Export category data for use in mega menu
export const categoryData = [{
  title: 'Bearings',
  categories: [{
    name: 'Agricultural Bearings',
    url: '/18m7/agricultural-bearings.html'
  }, {
    name: 'Appliance Bearings',
    url: '/18m7/appliance-bearings.html'
  }, {
    name: 'DLG Bearings',
    url: '/18m7/dlg-bearings.html'
  }, {
    name: 'Flanged Bearings',
    url: '/18m7/flanged-bearings.html'
  }, {
    name: 'Go-Kart Bearings',
    url: '/18m7/go-kart-bearings.html'
  }, {
    name: 'Insert Bearings',
    url: '/18m7/insert-bearings/uc-series.html'
  }, {
    name: 'Miniature Bearings',
    url: '/18m7/miniature-bearings.html'
  }, {
    name: 'Motorcycle Wheel Bearings',
    url: '/18m7/motorcycle-wheel-bearings.html'
  }, {
    name: 'Needle Bearings',
    url: '/18m7/needle-bearings.html'
  }, {
    name: 'R-Series Bearings',
    url: '/18m7/r-series-bearings.html'
  }, {
    name: 'Single Row Ball Bearings',
    url: '/18m7/single-row-ball-bearings/6000-ball-bearings.html'
  }, {
    name: 'Double Row Ball Bearings',
    url: '/18m7/double-row-ball-bearings/5200-series.html'
  }, {
    name: 'Taper Bearings',
    url: '/18m7/taper-roller-bearings.html'
  }, {
    name: 'Snowmobile Bearings',
    url: '/18m7/snowmobile-bearings.html'
  }, {
    name: 'Snow Blower / Snow Thrower Bearings',
    url: '/18m7/snow-blower-thrower-bearings-belts/snow-blower-thrower-bearings.html'
  }]
}, {
  title: 'Bearing Units',
  categories: [{
    name: 'Pillow Block Bearing Units',
    url: '/18m7/pillow-block-bearing-units/ucp-series-steel-units.html'
  }, {
    name: 'Plummer Block Bearing Units',
    url: '/18m7/plummer-block-bearing-units.html'
  }, {
    name: 'Take Up Bearings',
    url: '/18m7/take-up-bearings.html'
  }, {
    name: 'Tapped Base Bearings',
    url: '/18m7/tapped-base-bearings.html'
  }, {
    name: 'Round Flanged Cartridge Units',
    url: '/18m7/round-flanged-cartridge-units-ucfc-series.html'
  }]
}, {
  title: 'Flange Units',
  categories: [{
    name: '2-Bolt Flange Units',
    url: '/18m7/2-bolt-flange-unit/steel-2-bolt-flange-units.html'
  }, {
    name: '4-Bolt Flange Units',
    url: '/18m7/4-bolt-flange-units/steel-4-bolt-flange-units.html'
  }]
}, {
  title: 'Belts',
  categories: [{
    name: 'V-Belts',
    url: '/18m7/v-belts/type-a/type-a-v-belts.html'
  }, {
    name: 'Lawn Mower Belts',
    url: '/18m7/lawn-mower-bearings-belts/lawn-mower-belts.html'
  }, {
    name: 'Snow Blower / Snow Thrower Belts',
    url: '/18m7/snow-blower-thrower-bearings-belts/snow-blower-thrower-belts.html'
  }]
}, {
  title: 'Pulleys & Sheaves',
  categories: [{
    name: 'Pulleys & Sheaves',
    url: '/18m7/pulleys-sheaves.html'
  }, {
    name: 'Electric Motor Pulleys',
    url: '/18m7/pulleys-sheaves/electric-motor-pulleys/single-electric-motor-pulleys.html'
  }, {
    name: 'Variable Speed Pulleys',
    url: '/18m7/pulleys-sheaves/variable-speed-pulleys/vl-series.html'
  }]
}, {
  title: 'Chain & Sprockets',
  categories: [{
    name: 'Chain',
    url: '/18m7/chain/40-chain.html'
  }, {
    name: 'Sprockets',
    url: '/18m7/sprockets/50-sprockets/a-plate-sprockets.html'
  }]
}, {
  title: 'Couplings',
  categories: [{
    name: 'Couplings',
    url: '/18m7/couplings/jaw-coupling.html'
  }]
}, {
  title: 'Motors & Drives',
  categories: [{
    name: 'Electric Motors',
    url: '/18m7/electric-motors.html'
  }, {
    name: 'Controls and Drives',
    url: '/18m7/controls-and-drives.html'
  }]
}, {
  title: 'OEM / Equipment Parts',
  categories: [{
    name: 'Turf OEM Parts',
    url: '/18m7/turf-oem-parts/john-deere.html'
  }, {
    name: 'Lawn Mower Bearings',
    url: '/18m7/lawn-mower-bearings-belts/lawn-mower-bearings.html'
  }, {
    name: 'Lawn Mower Belts',
    url: '/18m7/lawn-mower-bearings-belts/lawn-mower-belts.html'
  }]
}, {
  title: 'Specials',
  categories: [{
    name: 'Special Sales',
    url: '/18m7/special-sales.html'
  }]
}, {
  title: 'Other',
  categories: [{
    name: 'Cutler Oven Bearings and Parts',
    url: '/18m7/cutler-oven-bearings-and-parts.html'
  }, {
    name: 'Miscellaneous',
    url: '/18m7/miscellaneous.html'
  }]
}];